import {useCallback, useEffect, useMemo, useState} from 'react';

import {ProcedureType} from 'graphql-client/__generated/graphqlGlobalTypes';
import {normalizeNameForUniqueness} from 'procedures/design/flowchartManagementUtils';
import {validateMethodName} from 'procedures/design/validation/methodValidationUtils';
import removeNulls from 'util/removeNulls';

import {ProcedureEditorContext} from './ProcedureEditorContext';
import {getMethodNamePlaceholder} from './procedureMethodNodeCommonConsts';

export interface MethodNameEditingState {
  isEditingName: boolean;
  setIsEditingName: (isEditing: boolean) => void;
  editingNameError: string | null;
  setEditingNameError: (error: string | null) => void;
  wrapperNoDragClass: string;
  allMethodNamesExcludingSelectedMethod: Array<string>;
  getDefaultNameWhenEmpty: () => string;
  onDraftNameChanged: (name: string) => void;
}

export function useMethodNameEditing(
  methodCid: string,
  procedureType: ProcedureType,
  setLegacyShouldPauseAutosave: (shouldPause: boolean) => void
): MethodNameEditingState {
  const {methodNamesInUse, editingNameMethodCid, setEditingNameMethodCid} =
    ProcedureEditorContext.useContext();

  const [editingNameError, setEditingNameError] = useState<string | null>(null);
  const isEditingName = editingNameMethodCid === methodCid;

  const setIsEditingName = useCallback(
    (newIsEditingName: boolean): void => {
      setEditingNameMethodCid(newIsEditingName ? methodCid : null);
      if (!newIsEditingName) {
        setEditingNameError(null);
      }
    },
    [methodCid, setEditingNameMethodCid]
  );

  useEffect(() => {
    setLegacyShouldPauseAutosave(isEditingName);
  }, [isEditingName, setLegacyShouldPauseAutosave]);

  const wrapperNoDragClass = editingNameMethodCid === methodCid ? 'nodrag' : '';

  const allMethodNamesExcludingSelectedMethod = useMemo(
    () => removeNulls(Object.values({...methodNamesInUse, [methodCid]: null})),
    [methodNamesInUse, methodCid]
  );

  const getDefaultNameWhenEmpty = useCallback(
    (): string =>
      normalizeNameForUniqueness(
        '',
        allMethodNamesExcludingSelectedMethod,
        getMethodNamePlaceholder(procedureType)
      ),
    [allMethodNamesExcludingSelectedMethod, procedureType]
  );

  const onDraftNameChanged = useCallback(
    (name: string): void => {
      setEditingNameError(validateMethodName(name, allMethodNamesExcludingSelectedMethod, procedureType));
    },
    [allMethodNamesExcludingSelectedMethod, procedureType]
  );

  return {
    isEditingName,
    setIsEditingName,
    editingNameError,
    setEditingNameError,
    wrapperNoDragClass,
    allMethodNamesExcludingSelectedMethod,
    getDefaultNameWhenEmpty,
    onDraftNameChanged,
  };
}
